import { useEffect, useMemo, useState } from "react";

import { UnsuccessfulFetchError } from "src/clients";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { GetAccountOrgsResponse } from "@atlassiansox/asc-stargate";
import OriginTracing from "@atlassiansox/origin-tracing";

import { DirectBuyOrgSetup } from "@/components/signup/post-order/src/components";
import {
    CMK_OFFERING_IDS,
    CMK_SUPPORT_URL,
    ufoEventPostOrderAccountOrgListNextPage,
    ufoEventPostOrderOrgActivation,
    orgBasedOfferingIdMap,
    OrgBasedProduct
} from "@/components/signup/utils";
import {
    ActivateDirectBuyOnOrgRequest,
    GetSignupAccountOrgDetailsResponse,
    GetSignupAccountOrgDetailsResponseDto,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import {
    AsyncResult,
    getIdSigninUrl,
    useSignupAccountOrgDetails
} from "@/libraries/signup/bxpkit-core";
import {
    COMMA,
    COMMA_REGEX
} from "@/templates/buy/cloud/post-order-provisioning/src";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

/**
 * Checks if the products array contains any CMK (Customer Managed Keys) variant.
 * @param products - Array of org-based products to check
 * @returns true if any CMK product is found, false otherwise
 */
export const hasCMKProduct = (products: OrgBasedProduct[]): boolean => {
    return products.some(
        (p) =>
            p === OrgBasedProduct.CUSTOMER_MANAGED_KEYS ||
            p === OrgBasedProduct.CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY
    );
};

/**
 * Returns the highest priority org product, with CMK (Customer Managed Keys) takes highest priority because CMK orders
 * skip site-setup and go directly to the Encryption Adminhub page.
 * @param products - Array of org-based products
 * @returns The prioritized product or the first product if no CMK is found
 */
export const getPrioritizedOrgProduct = (
    products: OrgBasedProduct[]
): OrgBasedProduct | undefined => {
    // CMK takes highest priority
    const cmkProduct = products.find(
        (p) =>
            p === OrgBasedProduct.CUSTOMER_MANAGED_KEYS ||
            p === OrgBasedProduct.CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY
    );
    if (cmkProduct) {
        return cmkProduct;
    }

    // Otherwise return first available
    return products[0];
};

/**
 * Checks if the given org has any active CMK entitlement (by offering ID).
 * Optionally narrows to a specific entitlement ID when provided.
 * Used to detect orgs that should be sent to the CMK support page rather than
 * the standard site-setup flow.
 */
const hasActiveCMKEntitlementOnOrg = (
    org: GetSignupAccountOrgDetailsResponseDto,
    entitlementId?: string
): boolean => {
    return (
        org.entitlementsByOfferingIds?.some(
            (entitlements) =>
                CMK_OFFERING_IDS.includes(entitlements.offeringId) &&
                (entitlements.entitlementIds?.length ?? 0) > 0 &&
                (entitlementId === undefined ||
                    entitlements.entitlementIds?.includes(entitlementId))
        ) ?? false
    );
};

export type DirectBuyOrgSetupContainerProps = {
    readonly orderId?: string;
    readonly transactionAccount?: string;
};

const getTraceId = (error: UnsuccessfulFetchError): string | null => {
    return error.response?.headers?.get?.("Atl-Traceid");
};

const DirectBuyOrgSetupContainer = (props: DirectBuyOrgSetupContainerProps) => {
    const imageAssets = {
        background:
            "https://wac.stg.internal.atlassian.com/dam/jcr:fa398d64-ddab-4e01-b3bf-90042efc7638/blue_wave_background.svg",
        browser:
            "https://dam-cdn.atl.orangelogic.com/AssetLink/31wo3kl8rv7o3167k4s3yu4k4e1r1twf.svg"
    };
    const searchParams = new URLSearchParams(window.location.search);
    const orderId = props.orderId || searchParams.get("orderId") || "";
    const offeringIdParam = searchParams.get("offeringId") || "";
    const preselectedOrgId = searchParams.get("orgId") || "";
    const entitlementId = searchParams.get("entitlementId") || "";
    const siteIdsParam = searchParams.get("siteIds") || "";
    const containsSiteProduct =
        searchParams.get("containsSiteProduct") === "true";
    const transactionAccount =
        props.transactionAccount ||
        searchParams.get("transactionAccount") ||
        "";

    // Memoize offeringIds to provide stable reference for downstream memos
    const offeringIds = useMemo(
        () => offeringIdParam.split(COMMA_REGEX),
        [offeringIdParam]
    );

    const orgBasedProducts = offeringIds
        .map(
            (offeringId) =>
                orgBasedOfferingIdMap[
                    offeringId as keyof typeof orgBasedOfferingIdMap
                ] as OrgBasedProduct
        )
        .filter((product) => !!product);

    const { requestInProgress, isUserLoggedIn } = useLoggedInUser();
    const stargateClient = useBxpWebStargateClient();

    const [isLoading, setIsLoading] = useState(true);
    const [orgIds, setOrgIds] = useState<GetAccountOrgsResponse>([]);
    const [orgs, setOrgs] = useState<GetSignupAccountOrgDetailsResponseDto[]>(
        []
    );
    const [error, setError] = useState(false);
    const [isLastItemLoading, setIsLastItemLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [traceId, setTraceId] = useState<string>();

    const PAGE_SIZE = 50;

    // Include CMK offering IDs so we can detect if selected org already has CMK entitlements
    // Memoize to prevent unnecessary re-fetches in useSignupAccountOrgDetails
    const allOfferingIds = useMemo(() => {
        return [...new Set([...offeringIds, ...CMK_OFFERING_IDS])];
    }, [offeringIds]);

    const orgDetails = useSignupAccountOrgDetails({
        offeringIds: allOfferingIds,
        skipNames: true,
        pageSize: PAGE_SIZE,
        preselectedOrgId
    }).catch((error) => {
        const responseTraceId = getTraceId(error as UnsuccessfulFetchError);
        if (responseTraceId) {
            setTraceId(responseTraceId);
        }
        return undefined;
    });

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const deferOrgActivationOnSiteSetup = checkFeatureFlagGate(
        featureFlags.signupPopBackBtn
    );

    // Shared helper: fire analytics and redirect to the CMK Adminhub setup page.
    const redirectToCMKSupportPage = (
        org: GetSignupAccountOrgDetailsResponseDto
    ) => {
        createAnalyticsEvent({
            eventName: "cmk-order-redirected-to-support",
            eventComponent: "direct-buy-org-setup-container",
            event: "redirected",
            eventType: "ui",
            action: "redirected",
            attributes: {
                offeringIds: offeringIds?.join(",") || "",
                orderId,
                orgId: org.id,
                entitlementId: entitlementId || "",
                alreadyActivated: true,
                redirectUrl: CMK_SUPPORT_URL
            }
        }).fire();
        window.location.assign(CMK_SUPPORT_URL);
    };

    // Redirect TWC or Strategy to /site-setup when org already contains entitlement ID
    const redirectUnfinishedCollectionEntitlementToSiteSetup = (
        orgs: GetSignupAccountOrgDetailsResponseDto[]
    ) => {
        // Turn off auto-redirect to site-setup when deferOrgActivationOnSiteSetup is on
        if (containsSiteProduct && deferOrgActivationOnSiteSetup) {
            return false;
        }

        // Check if this is a CMK order with site products where CMK is already activated.
        // This check looks for CMK entitlements by offering ID (not entitlement ID).
        const isCMKOrder = hasCMKProduct(orgBasedProducts);

        // Check if THIS SPECIFIC CMK entitlement is already activated.
        // Only redirect to support page if the CMK from THIS order is already activated.
        // If user has CMK from a different order, they still need to activate THIS CMK.
        if (isCMKOrder && containsSiteProduct && entitlementId) {
            const orgWithCMK = orgs.find((organization) =>
                hasActiveCMKEntitlementOnOrg(organization, entitlementId)
            );
            if (orgWithCMK) {
                redirectToCMKSupportPage(orgWithCMK);
                return true;
            }
        }

        const matchedOrg = orgs.find(
            (organization) =>
                entitlementId &&
                containsSiteProduct &&
                organization.entitlementsByOfferingIds?.some((entitlements) =>
                    entitlements.entitlementIds?.includes(entitlementId)
                )
        );

        if (matchedOrg) {
            // If the matched org already has an active CMK entitlement, redirect
            // to the CMK support page instead of site-setup, since CMK is
            // not activated via site-setup.
            const cmkRedirectEnabled = checkFeatureFlagGate(
                featureFlags.cmkRedirectExistingEntitlement
            );
            if (
                cmkRedirectEnabled &&
                hasActiveCMKEntitlementOnOrg(matchedOrg)
            ) {
                redirectToCMKSupportPage(matchedOrg);
                return true;
            }

            const search = new URLSearchParams(window.location.search);
            search.append("orgId", matchedOrg.id);
            const url = `/buy/cloud/site-setup?${search}`;
            window.location.assign(url);
            return true;
        }

        return false;
    };

    useEffect(() => {
        if (requestInProgress || AsyncResult.isPending(orgDetails)) {
            return;
        }
        if (!requestInProgress && !isUserLoggedIn) {
            // If user is not logged in, edirect user to the sign-in page
            const signInUserOrigin = new OriginTracing({
                product: "wac-org-setup-login"
            });
            const redirectUrl = getIdSigninUrl(signInUserOrigin.encode());

            setIsLoading(false);
            window.location.assign(redirectUrl);

            return;
        }

        if (AsyncResult.isError(orgDetails) || !orgDetails.value) {
            setError(true);
        } else {
            setOrgIds(orgDetails.value.accountOrgs.accountOrgs ?? []);
            let orgs: GetSignupAccountOrgDetailsResponseDto[] = [];
            if (orgDetails.value.orgDetails.length > 0) {
                orgs = orgDetails.value.orgDetails
                    .map((org) => org.organization)
                    .filter((org) => !!org);
            }
            // Filter only org that contains site ID params
            if (siteIdsParam) {
                const siteIds = siteIdsParam.split(COMMA_REGEX);
                orgs = orgs.filter((org) =>
                    siteIds.every((siteId) => org.siteIds?.includes(siteId))
                );
            }

            const isRedirected =
                redirectUnfinishedCollectionEntitlementToSiteSetup(orgs);
            if (isRedirected) {
                return;
            }

            setOrgs(orgs);
        }

        setIsLoading(false);
    }, [requestInProgress, orgDetails]);

    /* istanbul ignore next */
    const getNextPageOrgs = async () => {
        try {
            if (orgs.length < orgIds.length) {
                setIsLastItemLoading(true);
                ufoEventPostOrderAccountOrgListNextPage.addCustomData({
                    reactComponent: "direct-buy-org-setup-container"
                });
                const startIndex = orgs.length;
                const endIndex =
                    orgs.length + PAGE_SIZE < orgIds.length
                        ? orgs.length + PAGE_SIZE
                        : orgIds.length;
                const nextPageOrgsPromise = [];
                for (let i = startIndex; i < endIndex; i++) {
                    const orgId = orgIds[i];
                    if (orgId) {
                        nextPageOrgsPromise.push(
                            stargateClient
                                .getSignupAccountOrgDetails({
                                    orgId: orgId.id,
                                    offeringIds
                                })
                                .then((org) => org.organization)
                        );
                    }
                }
                const nextPageOrgs = await Promise.all(nextPageOrgsPromise);
                const updatedOrgs = orgs.concat(
                    nextPageOrgs.filter((org) => !!org)
                );
                setOrgs(updatedOrgs);

                redirectUnfinishedCollectionEntitlementToSiteSetup(updatedOrgs);

                ufoEventPostOrderAccountOrgListNextPage.addCustomData({
                    accountOrgs: JSON.stringify(orgs)
                });
                ufoEventPostOrderAccountOrgListNextPage.complete();
                setIsLastItemLoading(false);
            }
        } catch (error: unknown) {
            const responseTraceId = getTraceId(error as UnsuccessfulFetchError);
            if (responseTraceId) {
                setTraceId(responseTraceId);
            }
            ufoEventPostOrderAccountOrgListNextPage.error({
                errorName:
                    error instanceof Error ? error.name : "Unknown error",
                errorMessage:
                    error instanceof Error
                        ? error.message
                        : typeof error === "string"
                        ? error
                        : "Unknown error message"
            });
        }
    };

    /* istanbul ignore next */
    // eslint-disable-next-line complexity
    const activateForExistingOrg = async (data: { orgId: string }) => {
        try {
            setIsSubmitting(true);
            ufoEventPostOrderOrgActivation.addCustomData({
                product: orgBasedProducts.join(","),
                orgId: data.orgId,
                orderId,
                transactionAccountId: transactionAccount,
                reactComponent: "direct-buy-org-setup-container"
            });

            // Feature flag this and check if containsSiteProduct is true
            let provisioningRequestId = "defer-org-activation-on-site-setup";
            if (!containsSiteProduct || !deferOrgActivationOnSiteSetup) {
                provisioningRequestId = (
                    await stargateClient.activateDirectBuyOnOrg({
                        timezone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        region: "us",
                        orgId: data.orgId,
                        orderId,
                        transactionAccountId: transactionAccount,
                        webPlatform: "bxp-ui"
                    } as ActivateDirectBuyOnOrgRequest)
                ).provisioningRequestId;
            }

            const org = orgDetails.value?.orgDetails.find(
                (org: GetSignupAccountOrgDetailsResponse) =>
                    org.organization?.id === data.orgId
            );
            ufoEventPostOrderOrgActivation.addCustomData({
                provisioningRequestId
            });
            ufoEventPostOrderOrgActivation.complete();
            if (!org) {
                ufoEventPostOrderOrgActivation.error({
                    errorName: "Org not found",
                    errorMessage: `orgId ${data.orgId} not found in available org list`
                });
                throw new Error(
                    `orgId ${data.orgId} not found in available org list`
                );
            }

            const selectedOrg = org.organization;

            // If selected org already has an active CMK entitlement, redirect to
            // CMK support page instead of site-setup (CMK is not activated via site-setup)
            const cmkRedirectEnabled = checkFeatureFlagGate(
                featureFlags.cmkRedirectExistingEntitlement
            );
            if (
                cmkRedirectEnabled &&
                selectedOrg &&
                hasActiveCMKEntitlementOnOrg(selectedOrg)
            ) {
                redirectToCMKSupportPage(selectedOrg);
                return;
            }

            // CMK orders should always activate org because we skip site-setup
            // and go directly to Encryption Adminhub page
            const isCMKOrder = hasCMKProduct(orgBasedProducts);

            const shouldRedirectToSiteSetup =
                containsSiteProduct && !isCMKOrder;

            if (shouldRedirectToSiteSetup) {
                createAnalyticsEvent({
                    eventName: "redirectedToOrgSiteNamePage",
                    eventComponent: "direct-buy-org-setup-container",
                    event: "redirected",
                    eventType: "ui",
                    action: "redirected"
                }).fire();
                const orgId = data.orgId;
                const queryParams = new URLSearchParams({
                    orderId,
                    transactionAccount,
                    ...(offeringIds && offeringIds.length > 0
                        ? { offeringId: offeringIds.join(COMMA) }
                        : {}),
                    ...(siteIdsParam ? { siteIds: siteIdsParam } : {}),
                    ...(orgId ? { orgId } : {})
                });
                const url = `/buy/cloud/site-setup?${queryParams}`;
                window.location.assign(url);
            } else {
                createAnalyticsEvent({
                    eventName: "redirectedToConfirmation",
                    eventComponent: "direct-buy-org-setup-container",
                    event: "redirected",
                    eventType: "ui",
                    action: "redirected"
                }).fire();
                const product =
                    getPrioritizedOrgProduct(orgBasedProducts)?.toLowerCase() ||
                    "rovo";
                const queryParams = new URLSearchParams({
                    provisioningRequestId,
                    activateFor: "existing",
                    orgId: data.orgId,
                    orderId,
                    products: product,
                    ondemandurl: product,
                    platform: "ccp"
                });
                const url = `/ondemand/signup/confirmation?${queryParams}`;
                window.location.assign(url);
            }
        } catch (error) {
            setIsSubmitting(false);
            ufoEventPostOrderOrgActivation.error({
                errorName:
                    error instanceof Error ? error.name : "Unknown error",
                errorMessage:
                    error instanceof Error
                        ? error.message
                        : typeof error === "string"
                        ? error
                        : "Unknown error message"
            });
            const responseTraceId = getTraceId(error as UnsuccessfulFetchError);
            if (responseTraceId) {
                setTraceId(responseTraceId);
            }
            setError(true);
        }
    };

    return (
        <div>
            {!!isUserLoggedIn && (
                <DirectBuyOrgSetup
                    imageAssets={imageAssets}
                    error={error}
                    orgs={orgs}
                    transactionAccount={transactionAccount}
                    orderId={orderId}
                    isLoading={isLoading}
                    isLastItemLoading={isLastItemLoading}
                    isUserLoggedIn={isUserLoggedIn}
                    isSubmitting={isSubmitting}
                    activateForExistingOrg={activateForExistingOrg}
                    getNextPageOrgs={getNextPageOrgs}
                    orgBasedProduct={getPrioritizedOrgProduct(orgBasedProducts)}
                    offeringIds={offeringIds}
                    traceId={traceId}
                />
            )}
        </div>
    );
};

export default DirectBuyOrgSetupContainer;
